import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
import { useSubscription } from '../hooks/useSubscription'
import { User, LogOut, Settings, X, CreditCard, Clock, AlertTriangle, Crown } from 'lucide-react'

const UserProfile = ({ className = '', onModalClose }) => {
  const { user, signOut, loading } = useAuth()
  const {
    subscription,
    hasActiveSubscription,
    creditsRemaining,
    subscriptionExpiry,
    packageName,
    isExpiringSoon,
    loading: subscriptionLoading
  } = useSubscription()
  const [isModalOpen, setIsModalOpen] = useState(onModalClose ? true : false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleSignOut = async () => {
    setIsLoggingOut(true)
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      setIsLoggingOut(false)
      setIsModalOpen(false)
    }
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    if (onModalClose) {
      onModalClose()
    }
  }

  if (loading) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-bg-tertiary rounded-full animate-pulse" />
      </div>
    )
  }

  if (!user) {
    return null
  }

  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'
  const profilePicture = user.user_metadata?.avatar_url || user.user_metadata?.picture
  const initials = displayName
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getDaysRemaining = () => {
    if (!subscriptionExpiry) return 0
    const now = new Date()
    const expiry = new Date(subscriptionExpiry)
    const diffTime = expiry - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  const getStatusColor = () => {
    if (!hasActiveSubscription) return 'text-gray-400'
    if (isExpiringSoon) return 'text-yellow-500'
    if (creditsRemaining < 50) return 'text-orange-500'
    return 'text-green-500'
  }

  const getStatusText = () => {
    if (!hasActiveSubscription) return 'Tidak Aktif'
    if (isExpiringSoon) return 'Akan Berakhir'
    if (creditsRemaining < 50) return 'Kredit Rendah'
    return 'Aktif'
  }

  return (
    <div className={`relative ${className}`}>
      {/* User Button - Only Circle Avatar */}
      <button
        onClick={toggleModal}
        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-accent-primary transition-all duration-200 flex-shrink-0 touch-manipulation"
      >
        {profilePicture ? (
          <img 
            src={profilePicture} 
            alt={displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'flex'
            }}
          />
        ) : null}
        <div 
          className={`w-full h-full bg-accent-primary flex items-center justify-center ${
            profilePicture ? 'hidden' : 'flex'
          }`}
        >
          <span className="text-white text-xs sm:text-sm font-medium">{initials}</span>
        </div>
      </button>

      {/* Profile Modal using Portal */}
      {isModalOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-3 sm:p-4" 
            onClick={closeModal}
          >
            {/* Modal Content */}
            <div 
              className="bg-bg-secondary border border-border-primary rounded-xl shadow-2xl w-full max-w-sm sm:max-w-md mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border-primary">
                <h2 className="text-base sm:text-lg font-semibold text-text-primary">Profil Pengguna</h2>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-bg-tertiary rounded-lg transition-colors touch-manipulation"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-text-secondary" />
                </button>
              </div>

              {/* User Info */}
              <div className="p-4 sm:p-6">
                <div className="flex flex-col items-center text-center mb-4 sm:mb-6">
                  {/* Large Avatar */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden mb-3 sm:mb-4">
                    {profilePicture ? (
                      <img 
                        src={profilePicture} 
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-full bg-accent-primary flex items-center justify-center ${
                        profilePicture ? 'hidden' : 'flex'
                      }`}
                    >
                      <span className="text-white text-lg sm:text-xl font-medium">{initials}</span>
                    </div>
                  </div>
                  
                  {/* User Details */}
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-1">
                    {displayName}
                  </h3>
                  <p className="text-text-tertiary text-xs sm:text-sm mb-3 sm:mb-4 break-all">
                    {user.email}
                  </p>
                  
                  {/* Additional Info */}
                  {user.user_metadata?.provider && (
                    <div className="text-xs text-text-tertiary bg-bg-tertiary px-2 sm:px-3 py-1 rounded-full">
                      Masuk dengan {user.user_metadata.provider === 'google' ? 'Google' : user.user_metadata.provider}
                    </div>
                  )}
                </div>

                {/* Subscription Status Section */}
                <div className="mb-4 sm:mb-6">
                  <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Crown className="w-4 h-4 text-accent-primary" />
                        <span className="text-sm font-medium text-text-primary">Status Berlangganan</span>
                      </div>
                      <span className={`text-xs font-medium ${getStatusColor()}`}>
                        {getStatusText()}
                      </span>
                    </div>
                    
                    {subscriptionLoading ? (
                      <div className="space-y-2">
                        <div className="h-3 bg-bg-quaternary rounded animate-pulse"></div>
                        <div className="h-3 bg-bg-quaternary rounded w-2/3 animate-pulse"></div>
                      </div>
                    ) : hasActiveSubscription ? (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-text-tertiary">Paket:</span>
                          <span className="text-sm font-medium text-text-primary">{packageName}</span>
                        </div>
                        
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-text-tertiary">Kredit tersisa:</span>
                            <span className="text-sm font-medium text-text-primary">
                              {creditsRemaining?.toLocaleString('id-ID') || '0'}
                            </span>
                          </div>
                          <div className="w-full bg-bg-quaternary rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                creditsRemaining > 100 ? 'bg-green-500' :
                                creditsRemaining > 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{
                                width: `${Math.min(100, Math.max(5, (creditsRemaining / (subscription?.credits_total || 1000)) * 100))}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-text-tertiary">Berakhir:</span>
                          <div className="text-right">
                            <div className="text-sm font-medium text-text-primary">
                              {formatDate(subscriptionExpiry)}
                            </div>
                            <div className="text-xs text-text-tertiary">
                              {getDaysRemaining()} hari lagi
                            </div>
                          </div>
                        </div>
                        
                        {isExpiringSoon && (
                          <div className="flex items-center space-x-2 text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Berlangganan akan berakhir</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <CreditCard className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
                        <p className="text-xs text-text-tertiary mb-2">
                          Belum ada paket aktif
                        </p>
                        <button
                          onClick={() => {
                            closeModal()
                            window.location.href = '/subscription'
                          }}
                          className="bg-accent-primary text-white px-3 py-1.5 rounded-lg text-xs hover:bg-accent-primary/90 transition-colors"
                        >
                          Pilih Paket
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {/* Settings Button */}
                  <button className="w-full flex items-center justify-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 hover:bg-bg-tertiary transition-colors rounded-lg touch-manipulation">
                    <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-text-secondary" />
                    <span className="text-sm sm:text-base text-text-primary font-medium">Pengaturan</span>
                  </button>

                  {/* Sign Out Button */}
                  <button 
                    onClick={handleSignOut}
                    disabled={isLoggingOut}
                    className="w-full flex items-center justify-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 hover:bg-red-500/10 transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  >
                    <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                    <span className="text-sm sm:text-base text-red-400 font-medium">
                      {isLoggingOut ? 'Keluar...' : 'Keluar'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

UserProfile.propTypes = {
  className: PropTypes.string,
  onModalClose: PropTypes.func
}

export default UserProfile